import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import {
  faCar,
  faCheck,
  faExclamationCircle,
  faInfoCircle, faMinusCircle, faPlusCircle,
  faWrench,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import { PAFilterControl } from "../../_components/planning-assistant/singletons/pa-filter-control";
import { CommonModule } from "@angular/common";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from '@cloudfactorydk/ng2-tooltip-directive';
import { MatCheckbox } from "@angular/material/checkbox";
import { FormsModule } from "@angular/forms";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { faCarBuilding } from "@fortawesome/pro-duotone-svg-icons";

export interface BarData {
  description: string
  values: BarDataValue[]
  start_at_unit_amount: number
  style: 'filled' | 'bordered'
  filter_attributes: string[]
  options: {
    'Reisetechniker'?: boolean
  },
  external_idx?: number
  utilizationPercent?: number
}

export interface BarDataValue {
  color: string,
  unit_amount: number,
  tooltip: BarTooltip,
  icon?: 'loading_error' | 'finished' | 'home_travel' | null,
  style?: 'filled' | 'bordered' // Per-Segment Style-Override (überschreibt Bar-Style)
}

export interface BarOption {
  title: string,
  tooltip: string
}

export interface Deadline {
  at_value: number,
  description: string,
  color: string
}

interface Label {
  color: string,
  description: string
}

interface UnitAmountSteps {
  percentage: number,
  description: string
}

interface BarPercentage {
  from_percent: number
  to_percent: number,
  tooltip: BarTooltip,
  icon?: IconDefinition,
  color: string,
  style?: 'filled' | 'bordered'
}

export interface BarTooltip {
  infos: string[],
  errors: string[]
}

interface Bar {
  description: string;
  percentages: BarPercentage[];
  data: BarData;
}

@Component({
  imports: [CommonModule, FontAwesomeModule, TooltipModule, MatCheckbox, FormsModule, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle],
  selector: 'hawk-capacity-bars',
  templateUrl: './capacity-bars.component.html',
  styleUrls: ['./capacity-bars.component.scss', './../../_components/_shared/styles/common-styles.scss'],
  standalone: true
})
export class CapacityBarsComponent implements OnChanges, AfterViewChecked {

  @Input() barData: BarData[]
  @Input() measureUnit: string = '%'
  @Input() deadlines: Deadline[]
  @Input() labels: Label[]

  @Input() unitAmountStart: number = 0
  @Input() unitAmountEnd: number = 100
  @Input() unitAmountInterSteps: number = 1

  @Input() filter: string[] = []
  @Input() options: BarOption[] = []

  @Output() clickBarEvent = new EventEmitter<number>()
  @Output() changeOptionEvent = new EventEmitter<{idx: number, option: string, new_value: boolean}>()

  @ViewChild('barsScrollWrapper') barsScrollWrapper: ElementRef<HTMLDivElement>

  public unitAmountSteps: UnitAmountSteps[] = []
  public bars: Bar[] = []
  public activeFilter: string = ''

  // Erweiterter Bereich für Bars die unter unitAmountStart starten (geclamped auf 0)
  public effectiveUnitAmountStart: number = 0
  public barsContainerHeightPx: number = 340
  private needsInitialScroll = false

  public icons = {
    loading_error: faExclamationCircle,
    finished: faCheck,
    home_travel: faCarBuilding
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateEffectiveStart()
    this.updateUnitAmountSteps()
    this.updateBarPercentages()
  }

  ngAfterViewChecked(): void {
    // Nach dem Rendern zum Anfang scrollen (zeigt 20:00 - unitAmountStart)
    if (this.needsInitialScroll && this.barsScrollWrapper?.nativeElement) {
      this.barsScrollWrapper.nativeElement.scrollTop = 0
      this.needsInitialScroll = false
    }
  }

  // Berechnet den effektiven Startpunkt basierend auf allen Bar-Starts (min 0)
  private updateEffectiveStart(): void {
    let min_start = this.unitAmountStart
    if (this.barData?.length) {
      for (const bar of this.barData) {
        if (bar.start_at_unit_amount < min_start) {
          min_start = bar.start_at_unit_amount
        }
      }
    }
    const new_effective = Math.max(0, min_start)

    if (new_effective !== this.effectiveUnitAmountStart) {
      this.needsInitialScroll = true
    }

    this.effectiveUnitAmountStart = new_effective
    this.barsContainerHeightPx = this.computeContainerHeight()
  }

  // Container-Höhe dynamisch berechnen basierend auf dem effektiven Bereich
  private computeContainerHeight(): number {
    const is_small_screen = window.innerWidth <= 1920
    const base_height = is_small_screen ? 300 : 340
    const base_bar_area = is_small_screen ? (300 - 30 - 20) : (340 - 60 - 20)

    const visible_range = this.unitAmountEnd - this.unitAmountStart
    const effective_range = this.unitAmountEnd - this.effectiveUnitAmountStart

    if (effective_range <= visible_range) return base_height

    const extended_bar_area = base_bar_area * effective_range / visible_range
    return base_height + (extended_bar_area - base_bar_area)
  }

  // Hilfsmethode für Deadline-Positionierung im Template
  getDeadlinePercentage(deadline: Deadline): number {
    const range = this.unitAmountEnd - this.effectiveUnitAmountStart
    return ((deadline.at_value - this.effectiveUnitAmountStart) / range) * 100
  }

  barIsShown(bar: Bar): boolean {
    if (!this.activeFilter) {
      return true
    } else {
      return !!bar.data.filter_attributes.find(attr => attr == this.activeFilter)
    }
  }

  private updateUnitAmountSteps() {
    const res: UnitAmountSteps[] = []
    const visible_range = this.unitAmountEnd - this.unitAmountStart
    const effective_range = this.unitAmountEnd - this.effectiveUnitAmountStart
    // Schrittgröße aus dem sichtbaren Bereich berechnen (z.B. 2 Stunden)
    const step_value = visible_range / (this.unitAmountInterSteps + 1)

    // Erstes Label am nächsten Schritt-Raster ab effectiveStart
    const first_aligned = Math.ceil(this.effectiveUnitAmountStart / step_value) * step_value

    for (let value = first_aligned; value <= this.unitAmountEnd; value += step_value) {
      const percentage = ((value - this.effectiveUnitAmountStart) / effective_range) * 100
      res.push({
        percentage: percentage,
        description: `${value}${this.measureUnit}`
      })
    }
    this.unitAmountSteps = res
  }

  private updateBarPercentages() {
    this.bars = this.barData.map(bar_item => {
      return {
        percentages: this.barDataItemToBarPercentages(bar_item),
        description: bar_item.description,
        data: bar_item
      }
    })
  }

  private barDataItemToBarPercentages(bar_item: BarData): BarPercentage[] {
    const percentages: BarPercentage[] = []
    const range = this.unitAmountEnd - this.effectiveUnitAmountStart

    let current_percentage = ((bar_item.start_at_unit_amount - this.effectiveUnitAmountStart) / range) * 100
    for (const value of bar_item.values) {
      percentages.push({
        from_percent: current_percentage,
        to_percent: current_percentage += (value.unit_amount / range) * 100,
        icon: value.icon ? this.icons[value.icon] : null,
        tooltip: value.tooltip,
        color: value.color,
        style: value.style
      })
    }

    return percentages
  }

  getFilterInstanceAmount(filter: string): number {
    return this.bars.filter(bar => bar.data.filter_attributes.includes(filter)).length
  }

  // Position für das Auslastungs-Badge: direkt über dem obersten Segment der Bar
  getBarTopPercent(bar: Bar): number {
    if (!bar.percentages.length) return 0
    return Math.max(...bar.percentages.map(percentage => percentage.to_percent))
  }

  getUtilizationClass(percent: number): 'high' | 'medium' | 'low' {
    if (percent > 80) return 'low'
    if (percent < 50) return 'high'
    if (percent >= 50 && percent <= 80) return 'medium'
    return 'low'
  }

  protected readonly faCar = faCar;
  protected readonly faCheck = faCheck;
  protected readonly faWrench = faWrench;
  protected readonly PAFilterControl = PAFilterControl;
  protected readonly faInfoCircle = faInfoCircle;

  protected readonly faMinusCircle = faMinusCircle;
  protected readonly faPlusCircle = faPlusCircle;
}
